import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, Clock, MapPin, Stethoscope, ChevronRight, ChevronDown, X, HeartHandshake, Filter, Star, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { useMultiProfile } from "@/auth/MultiProfileContext";
import StarRating from "@/components/StarRating";

const formatWhen = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "en" ? "en-US" : "es-CO", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const formatTimeOnly = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "es-CO", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const formatDateOnly = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-CO", { weekday: "short", day: "2-digit", month: "short" });
  } catch { return iso; }
};

const BookingModal = ({ slot, onClose, onBooked }) => {
  const { t, lang } = useI18n();
  const r = t.redApoyo;
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  if (!slot) return null;
  const proLabel = slot.professional_business || slot.professional_name || "—";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post(`/appointments/${slot.id}/book`, { topic: topic.trim(), notes: notes.trim() });
      onBooked();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(d === "APPT_LIMIT" ? t.community.apptLimit : formatApiError(err));
    }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="booking-modal">
      <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-6 border-b border-gray-100">
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center" data-testid="booking-close">
            <X size={16} />
          </button>
          <p className="eyebrow text-orange">{r.bookTitle}</p>
          <h3 className="font-display text-2xl text-teal-deep mt-1 leading-tight pr-8">{r.bookTitle}</h3>
          <p className="text-sm text-teal-soft mt-2">{r.bookSub.replace("{pro}", proLabel)}</p>
          <div className="mt-4 bg-cream rounded-2xl px-4 py-3 text-sm">
            <div className="flex items-center gap-2 text-teal-deep font-bold"><Calendar size={14} /> {formatWhen(slot.start_at, lang)}</div>
            <div className="flex items-center gap-2 text-teal-soft mt-1"><Clock size={14} /> {slot.duration_min} min</div>
          </div>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 overflow-y-auto" data-testid="booking-form">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{r.topicLabel}</label>
            <input required maxLength={240} minLength={4} className="field-input" placeholder={r.topicPlaceholder} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="booking-topic" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{r.notesLabel}</label>
            <textarea rows={3} maxLength={600} className="field-input" placeholder={r.notesPlaceholder} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes" />
          </div>
          {error && <p className="text-red-600 text-sm" data-testid="booking-error">{error}</p>}
          <button type="submit" disabled={loading} className="btn-orange w-full justify-center" data-testid="booking-submit">
            {loading ? r.booking : r.confirmBook}
          </button>
        </form>
      </div>
    </div>
  );
};

const ProfessionalAccordion = ({ pro, defaultOpen, onBook }) => {
  const { t, lang } = useI18n();
  const r = t.redApoyo;
  const [open, setOpen] = useState(!!defaultOpen);
  const [profile, setProfile] = useState(null); // fetched bio + rating
  const proLabel = pro.business || pro.name || "—";

  // Lazy-load the full professional profile the first time the accordion is opened
  useEffect(() => {
    if (!open || profile || !pro.profile_id) return;
    let cancelled = false;
    api.get(`/entrepreneurs/${pro.profile_id}`)
      .then(({ data }) => { if (!cancelled) setProfile(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, profile, pro.profile_id]);

  // Group slots by date for nicer rendering
  const grouped = useMemo(() => {
    const map = new Map();
    pro.slots.forEach((s) => {
      const key = (s.start_at || "").slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return Array.from(map.entries()).map(([k, list]) => ({ date: k, list }));
  }, [pro.slots]);

  return (
    <article className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid={`pro-card-${pro.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left p-5 flex items-center gap-4 hover:bg-cream/60 transition-colors"
        data-testid={`pro-toggle-${pro.id}`}
      >
        <span className="w-12 h-12 rounded-full bg-orange/15 text-orange flex items-center justify-center shrink-0 overflow-hidden">
          {pro.avatar ? (
            <img src={pro.avatar} alt={proLabel} className="w-full h-full object-cover" />
          ) : (
            <Stethoscope size={18} />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl text-teal-deep leading-tight truncate">{proLabel}</h3>
          <div className="flex items-center gap-2 flex-wrap text-xs text-teal-soft mt-1">
            <span className="uppercase tracking-wider font-bold">{t.categories[pro.category] || pro.category}</span>
            {pro.city && (<><span>·</span><span className="inline-flex items-center gap-1"><MapPin size={11} /> {pro.city}</span></>)}
            <span>·</span>
            <span className="text-orange font-bold">{pro.slots.length} {r.availableLabel}</span>
          </div>
        </div>
        <span className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}>
          <ChevronDown size={20} className="text-teal-soft" />
        </span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4" data-testid={`pro-slots-${pro.id}`}>
          {/* Mini profile: bio + stars + view-full link */}
          {profile && (
            <div className="bg-cream rounded-2xl p-4 mb-4" data-testid={`pro-mini-${pro.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                {(profile.ratings_count || 0) > 0 ? (
                  <div className="inline-flex items-center gap-2" data-testid={`pro-rating-${pro.id}`}>
                    <StarRating value={profile.avg_rating || 0} size={14} />
                    <span className="text-xs text-teal-soft">
                      {profile.avg_rating?.toFixed(1)} · {profile.ratings_count} {t.ratings.reviewPlural || "reviews"}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-teal-soft italic inline-flex items-center gap-1">
                    <Star size={12} /> {r.noRatingsYet}
                  </span>
                )}
                <Link
                  to={`/entrepreneur/${pro.profile_id}`}
                  className="text-xs text-orange hover:text-teal-deep font-bold uppercase tracking-wider ml-auto inline-flex items-center gap-1"
                  data-testid={`pro-view-${pro.id}`}
                >
                  {r.viewFullProfile} <ChevronRight size={11} />
                </Link>
              </div>
              {profile.description && (
                <p className="text-sm text-teal-soft mt-3 leading-relaxed line-clamp-3">{profile.description}</p>
              )}
            </div>
          )}

          {grouped.map(({ date, list }) => (
            <div key={date} className="mt-5">
              <div className="text-xs font-bold uppercase tracking-wider text-teal-deep mb-3 flex items-center gap-2">
                <Calendar size={12} className="text-orange" /> {formatDateOnly(date + "T12:00:00", lang)}
                <span className="text-teal-soft font-normal normal-case tracking-normal">· {list.length} {list.length === 1 ? "cupo" : "cupos"}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {list.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onBook(s)}
                    title={s.topic_hint || ""}
                    className="group relative bg-white hover:bg-teal-deep border border-gray-200 hover:border-teal-deep rounded-lg px-3 py-2.5 text-left transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                    data-testid={`slot-chip-${s.id}`}
                  >
                    <div className="flex items-center gap-1.5 text-teal-deep group-hover:text-white">
                      <Clock size={12} className="text-orange group-hover:text-orange" />
                      <span className="font-display font-bold text-base leading-none">{formatTimeOnly(s.start_at, lang)}</span>
                    </div>
                    <div className="text-[11px] text-teal-soft group-hover:text-white/80 mt-1 leading-tight">
                      {s.duration_min} min
                    </div>
                    <ChevronRight size={12} className="absolute top-2 right-2 text-orange opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

const JoinProCta = () => {
  const { t } = useI18n();
  const r = t.redApoyo;
  const { user } = useAuth();
  const { items: profiles } = useMultiProfile();
  const proProfile = useMemo(() => profiles.find((p) => p.kind === "professional"), [profiles]);

  let title, sub, href, label;
  if (!user) {
    title = r.joinTitleGuest;
    sub = r.joinSubGuest;
    href = "/register/account?kind=professional";
    label = r.joinCtaGuest;
  } else if (!proProfile) {
    title = r.joinTitleMember;
    sub = r.joinSubMember;
    href = "/profiles/new?kind=professional";
    label = r.joinCtaMember;
  } else {
    title = r.joinTitlePro;
    sub = r.joinSubPro;
    href = `/profiles/${proProfile.id}/agenda`;
    label = r.joinCtaPro;
  }

  return (
    <div
      className="container-tight pt-10"
      data-testid="red-apoyo-join-cta"
    >
      <div className="relative overflow-hidden rounded-3xl border border-orange/30 bg-gradient-to-br from-teal-deep via-teal-deep to-[#0f3a3d] text-white shadow-xl">
        <div className="absolute -right-16 -top-16 w-64 h-64 bg-orange/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-orange/10 rounded-full blur-2xl pointer-events-none" />
        <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-orange/20 text-orange flex items-center justify-center shrink-0">
            <Sparkles size={26} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-orange">{r.joinEyebrow}</p>
            <h2 className="font-display text-2xl md:text-3xl leading-tight mt-1">{title}</h2>
            <p className="text-white/80 mt-2 text-sm md:text-base">{sub}</p>
          </div>
          <div className="shrink-0">
            <Link
              to={href}
              className="btn-orange justify-center inline-flex items-center gap-2 shadow-lg shadow-orange/25"
              data-testid="red-apoyo-join-cta-btn"
            >
              <HeartHandshake size={16} /> {label}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const RedApoyo = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [city, setCity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [bookSlot, setBookSlot] = useState(null);
  const [toast, setToast] = useState("");
  const autoOpenSlotRef = React.useRef(false);

  const categoryKeys = useMemo(() => Object.keys(t.categories), [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 120 };
      if (category && category !== "all") params.category = category;
      if (city) params.city = city;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + "T23:59").toISOString();
      const { data } = await api.get("/appointments/available", { params });
      setItems(data.items || []);
    } catch (err) {
      console.error("redApoyo load failed", formatApiError(err));
    } finally { setLoading(false); }
  }, [category, city, dateFrom, dateTo]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  // Deep-link: after login redirects back with ?slot=<id>, auto-open the booking modal.
  useEffect(() => {
    if (!user || autoOpenSlotRef.current) return;
    const sid = searchParams.get("slot");
    if (!sid) return;
    const target = items.find((s) => s.id === sid);
    if (target) {
      autoOpenSlotRef.current = true;
      setBookSlot(target);
      const next = new URLSearchParams(searchParams);
      next.delete("slot");
      setSearchParams(next, { replace: true });
    }
  }, [user, items, searchParams, setSearchParams]);

  // Group slots by professional, preserving first-occurrence order (already sorted by start_at from API)
  const professionals = useMemo(() => {
    const map = new Map();
    items.forEach((s) => {
      const key = s.professional_id;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          profile_id: s.professional_profile_id,
          name: s.professional_name,
          business: s.professional_business,
          category: s.professional_category,
          city: s.professional_city,
          avatar: s.professional_avatar,
          slots: [],
        });
      }
      map.get(key).slots.push(s);
    });
    // Sort each pro's slots by start_at ascending (defensive)
    return Array.from(map.values()).map((p) => ({
      ...p,
      slots: [...p.slots].sort((a, b) => (a.start_at < b.start_at ? -1 : 1)),
    }));
  }, [items]);

  if (user === null) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  // Public: anyone can browse RED Online. Booking still gates behind login
  // (handled inside <BookingModal /> and the "Book" click on each slot).

  const onBooked = () => {
    setBookSlot(null);
    setToast(t.redApoyo.bookSuccess);
    setTimeout(() => setToast(""), 4500);
    load();
  };

  const requestBook = (slot) => {
    if (!user) {
      // Redirect to login and come back to the same slot
      const returnUrl = encodeURIComponent(`/red-apoyo?slot=${slot.id}`);
      window.location.href = `/login?next=${returnUrl}`;
      return;
    }
    setBookSlot(slot);
  };

  return (
    <section className="section bg-cream min-h-[80vh]">
      <JoinProCta />
      <div className="container-tight">
        <div className="text-center max-w-3xl mx-auto mt-10">
          <p className="eyebrow text-orange">{t.redApoyo.heroEyebrow}</p>
          <h1 className="font-display text-5xl md:text-6xl text-teal-deep mt-3 leading-tight">{t.redApoyo.heroTitle}</h1>
          <p className="mt-4 text-teal-soft text-lg">{t.redApoyo.heroSub}</p>
          <div className="mt-6 flex justify-center">
            {user ? (
              <Link to="/red-apoyo/mis-citas" className="text-sm text-teal hover:text-orange font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="my-bookings-link">
                <HeartHandshake size={14} /> {t.redApoyo.myBookingsTitle}
              </Link>
            ) : (
              <Link to="/register/account" className="text-sm text-teal hover:text-orange font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="join-to-book-link">
                <HeartHandshake size={14} /> Únete gratis para agendar citas
              </Link>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-10 bg-white rounded-2xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="redapoyo-filters">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{t.redApoyo.filterCategory}</label>
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="filter-category">
              <option value="all">{t.directory.all}</option>
              {categoryKeys.map((k) => <option key={k} value={k}>{t.categories[k]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{t.redApoyo.filterCity}</label>
            <input className="field-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Odessa…" data-testid="filter-city" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{t.redApoyo.filterFrom}</label>
            <input type="date" className="field-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{t.redApoyo.filterTo}</label>
            <input type="date" className="field-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" />
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-teal-soft inline-flex items-center justify-center gap-2 w-full" data-testid="results-count">
          <Filter size={12} /> {professionals.length} {t.redApoyo.professionalsCountLabel} · {items.length} {t.redApoyo.availableSlotsLabel}
        </div>

        <div className="mt-8 max-w-3xl mx-auto space-y-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={`sk-${i}`} className="bg-white rounded-2xl h-24 animate-pulse" />
            ))
          ) : professionals.length === 0 ? (
            <div className="text-center py-12 md:py-16 px-4" data-testid="redapoyo-empty-state">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-orange/10 text-orange mx-auto mb-5 flex items-center justify-center">
                <Calendar size={36} />
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-teal-deep mb-3">
                Aún no hay horarios disponibles
              </h3>
              <p className="text-teal-soft max-w-xl mx-auto mb-6">
                Los profesionales de la RED están construyendo sus agendas. Vuelve pronto o registra tu perfil profesional para donar tu tiempo.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link
                  to={user ? "/profiles/new?kind=professional" : "/register/account?kind=professional"}
                  className="btn-orange"
                  data-testid="redapoyo-empty-cta-pro"
                >
                  <HeartHandshake size={14} /> Soy profesional, quiero donar mi tiempo
                </Link>
                <Link to="/directory" className="btn-teal-outline" data-testid="redapoyo-empty-cta-directory">
                  <ChevronRight size={14} /> Explorar el directorio
                </Link>
              </div>
              <p className="text-xs text-teal-soft mt-6">
                💡 Tip: intenta ampliar el rango de fechas o quitar filtros arriba.
              </p>
            </div>
          ) : (
            professionals.map((p, idx) => (
              <ProfessionalAccordion
                key={p.id}
                pro={p}
                defaultOpen={idx === 0}
                onBook={requestBook}
              />
            ))
          )}
        </div>
      </div>

      <BookingModal slot={bookSlot} onClose={() => setBookSlot(null)} onBooked={onBooked} />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-teal-deep text-white px-5 py-3 rounded-full shadow-lg z-50 flex items-center gap-2" data-testid="redapoyo-toast">
          <HeartHandshake size={16} className="text-orange" /> {toast}
        </div>
      )}
    </section>
  );
};

export default RedApoyo;
